import React, { useCallback, useMemo } from 'react';

import styles from './AccessGroups.module.scss';
import { getAccessGroupColumnSort, AccessGroup, getAccessGroupFilters, AccessGroupTableName, getAccessGroupRoles } from './store';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { useAppSelector } from '../../../hooks/react-redux';
import { Toggle } from '../../shared/toggle';
import { AccessGroupTableHeader } from './AccessGroupTableHeader';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';
import { getAllRoles, getFetchingRoles } from '../roles/store';

interface AccessGroupRolesTableProps {
    roleIds: number[];
    updateGroup: (key: keyof AccessGroup, ids: number[]) => void;
    scrollableHeight: number;
}

export const AccessGroupRolesTable: React.FC<AccessGroupRolesTableProps> = ({ roleIds, updateGroup, scrollableHeight }) => {

    const columnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.ROLE));
    const filters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.ROLE));
    const allRoles = useAppSelector(getAllRoles);
    const rolesToDisplay = useAppSelector(getAccessGroupRoles);
    const isFetchingRoles = useAppSelector(getFetchingRoles);

    const toggleRoleInAccessGroup = useCallback((roleId: number, value: boolean) => {
        const updatedRoleIds = !value ? roleIds.filter(id => id !== roleId) : [...roleIds, roleId];
        updateGroup('roleIds', updatedRoleIds);
    }, [roleIds, updateGroup]);

    const roleInGroup = useCallback((roleId: number) => roleIds.includes(roleId), [roleIds]);

    const allSelected = useMemo(() => allRoles.every(({ roleId }) => roleIds.includes(roleId!)), [roleIds, allRoles]);

    const toggleSelectAll = useCallback(() => {
        const updatedRoleIds = allSelected ? [] : allRoles.map(({ roleId }) => roleId!);
        updateGroup('roleIds', updatedRoleIds);
    }, [allSelected, allRoles, updateGroup]);

    const tableHeaders = [
        { width: '80%', title: 'Name', column: 'roleName' },
        { width: '20%', title: 'Included', column: 'roleIncluded' }
    ];

    return (
        <div className={styles.accessGroupContentWrapper}>
            <AccessGroupTableHeader
                allSelected={allSelected}
                toggleSelectAll={toggleSelectAll}
                textFilterColumns={['roleName']}
                tableHeaders={tableHeaders}
                selectAllLabel='Roles'
                filters={filters}
                columnSort={columnSort}
                tableName={AccessGroupTableName.ROLE}
            />
            <Scrollable height={`${scrollableHeight}px`}>
                {isFetchingRoles ? <div className={styles.tableLoadingDots} style={{ height: scrollableHeight * 2 }}><LoadingDots /></div> :
                    rolesToDisplay.map(({ roleId, roleName }) => {
                        const inGroup = roleInGroup(roleId!);
                        return (
                            <div key={roleId} className={styles.tableRow}>
                                <div className={styles.tableCell} style={{ width: '80%' }}>{roleName}</div>
                                <div className={styles.tableCell} style={{ width: '20%' }}>
                                    <Toggle
                                        onChange={value => toggleRoleInAccessGroup(roleId!, value)}
                                        checked={inGroup}
                                    />
                                </div>
                            </div>
                        );
                    })
                }
            </Scrollable>
        </div>
    );
};
